import React, { useEffect, useState } from 'react';
import Modal from 'components/Layout/Modal';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { titleCaseString } from 'utils/helpers';
import TextInput from 'components/form/TextInput';
import Location from 'components/form/Location';
import { isEqual } from 'lodash';
import Button from 'components/form/Button';
import Label from 'components/form/Label';
import { useDispatch } from 'react-redux';
import {
  fetchProfessionalDetails,
  fetchProfile,
  handelProfessionalDetails,
  showSuccess,
} from 'store/slices';
import Loader from 'components/Layout/Loader';

const PreviewProfessionalDetailsPopup = ({ onHide }) => {
  const dispatch = useDispatch();
  const [btnLoading, setBtnLoading] = useState(false);
  const [professionalDetails, setProfessionalDetails] = useState([]);
  const [isLoader, setIsLoader] = useState(true);

  const getProfessionalDetails = async () => {
    const response = await dispatch(fetchProfessionalDetails());
    if (response?.status === 200) {
      setProfessionalDetails(response?.data);
    }
    setIsLoader(false);
  };

  useEffect(() => {
    getProfessionalDetails();
  }, []);

  const handelValue = (value) =>
    value && value !== '--Not Applicable--' ? value : '';

  const emptyExperience = {
    id: '',
    professional_institution_name: '',
    professional_department: '',
    professional_designation: '',
    // professional_address: '',
    professional_country: '',
    professional_state: '',
    professional_experience: '',
    industry_experience: '',
  };

  const professionalArray =
    Array.isArray(professionalDetails) && professionalDetails?.length > 0
      ? professionalDetails?.map((item) => ({
          id: item.id || '',
          professional_institution_name: handelValue(item.institute_name),
          professional_department: handelValue(item.department),
          professional_designation: handelValue(item.designation),
          // professional_address: handelValue(item.address),
          professional_country: handelValue(item.country),
          professional_state: handelValue(item.state),
          professional_experience: handelValue(item.professional_experience),
          industry_experience: handelValue(item.industry_experience),
        }))
      : [emptyExperience];

  const initialValues = {
    experiences: professionalArray,
  };

  const validationSchema = Yup.object().shape({
    experiences: Yup.array().of(
      Yup.object().shape({
        professional_institution_name: Yup.string().required(
          'Institution name is required.'
        ),
        professional_department: Yup.string().required(
          'Department is required.'
        ),
        professional_designation: Yup.string().required(
          'Designation is required.'
        ),
        // professional_address: Yup.string().required('Address is required.'),
        professional_country: Yup.string().required('Country is required.'),
        professional_state: Yup.string().required('State is required.'),
        professional_experience: Yup.string().required(
          'Professional experience is required.'
        ),
        industry_experience: Yup.string().required(
          'Industry experience is required.'
        ),
      })
    ),
  });

  const handelSave = async (values) => {
    setBtnLoading(true);
    const payload = { experiences: JSON.stringify(values.experiences) };
    const response = await dispatch(handelProfessionalDetails(payload));
    if (response?.status === 200) {
      dispatch(showSuccess(response.message));
      onHide();
      setBtnLoading(false);
      await dispatch(fetchProfile());
    }
    setBtnLoading(false);
  };

  return (
    <Modal
      titleBesideClose="Professional Details"
      titleBesideCloseClassName="text-16-400-20 color-1717"
      isTitleVerticalCenter
      isTitleBorder
      onHide={onHide}
      size="xl"
    >
      <div className="cp-20">
        {isLoader ? (
          <div>
            <Loader size="md" />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handelSave(values);
            }}
          >
            {({
              values,
              errors,
              handleChange,
              setFieldValue,
              handleSubmit,
              resetForm,
            }) => (
              <form>
                <FieldArray name="experiences">
                  {({ push, remove }) => (
                    <>
                      {values.experiences.map((exp, idx) => (
                        <div key={idx}>
                          <div className="row">
                            <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Institution/Organization Name"
                                placeholder="Institution/Organization Name"
                                id={`experiences[${idx}].professional_institution_name`}
                                value={exp.professional_institution_name}
                                error={
                                  errors.experiences?.[idx]
                                    ?.professional_institution_name
                                }
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Department"
                                placeholder="Department"
                                id={`experiences[${idx}].professional_department`}
                                value={exp.professional_department}
                                onChange={handleChange}
                                error={
                                  errors.experiences?.[idx]
                                    ?.professional_department
                                }
                              />
                            </div>
                            <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Designation"
                                placeholder="Designation"
                                id={`experiences[${idx}].professional_designation`}
                                value={exp.professional_designation}
                                onChange={handleChange}
                                error={
                                  errors.experiences?.[idx]
                                    ?.professional_designation
                                }
                              />
                            </div>
                            {/* <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Address"
                                placeholder="Address"
                                id={`experiences[${idx}].professional_address`}
                                value={exp.professional_address}
                                onChange={handleChange}
                                error={
                                  errors.experiences?.[idx]
                                    ?.professional_address
                                }
                              />
                            </div> */}
                            <div className="col-md-4 cmb-22">
                              <Label label="Country" />
                              <Location
                                type="country"
                                data={{
                                  id: `experiences[${idx}].professional_country`,
                                  placeholder: 'Country/Province',
                                  value: exp.professional_country,
                                  error:
                                    errors.experiences?.[idx]
                                      ?.professional_country,
                                  onChange: handleChange,
                                }}
                              />
                            </div>
                            <div className="col-md-4 cmb-22">
                              <Label label="State" />
                              <Location
                                type="state"
                                data={{
                                  id: `experiences[${idx}].professional_state`,
                                  placeholder: 'State',
                                  value: exp.professional_state,
                                  error:
                                    errors.experiences?.[idx]
                                      ?.professional_state,
                                  countryId: exp.professional_country,
                                  onChange: handleChange,
                                  disabled: !exp.professional_country,
                                }}
                              />
                            </div>
                            <div className="col-md-4 cmb-22"></div>
                            <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Professional Experience"
                                placeholder="Professional Experience"
                                id={`experiences[${idx}].professional_experience`}
                                value={exp.professional_experience}
                                onChange={(e) => {
                                  setFieldValue(
                                    `experiences[${idx}].professional_experience`,
                                    titleCaseString(e.target.value)
                                  );
                                }}
                                error={
                                  errors.experiences?.[idx]
                                    ?.professional_experience
                                }
                              />
                            </div>
                            <div className="col-md-4 cmb-22">
                              <TextInput
                                label="Industry Experience"
                                placeholder="Industry Experience"
                                id={`experiences[${idx}].industry_experience`}
                                value={exp.industry_experience}
                                onChange={(e) => {
                                  setFieldValue(
                                    `experiences[${idx}].industry_experience`,
                                    titleCaseString(e.target.value)
                                  );
                                }}
                                error={
                                  errors.experiences?.[idx]?.industry_experience
                                }
                              />
                            </div>
                            {values.experiences.length > 1 && (
                              <div className="col-md-4 cmb-22 d-flex justify-content-end align-item-center">
                                <Button
                                  text=""
                                  icon={<i class="bi bi-trash"></i>}
                                  btnStyle="danger-outline"
                                  className="cps-10 cpe-10 h-35"
                                  onClick={() => remove(idx)}
                                />
                              </div>
                            )}
                            <hr />
                          </div>
                        </div>
                      ))}

                      <div className="d-flex justify-content-start gap-4 mt-3">
                        <Button
                          text="Submit"
                          btnStyle="primary-dark"
                          className="cps-40 cpe-40 h-40"
                          onClick={handleSubmit}
                          btnLoading={btnLoading}
                          disabled={
                            btnLoading || isEqual(values, initialValues)
                          }
                        />

                        <Button
                          text="+ Add Experience"
                          btnStyle="primary-outline1"
                          className="cps-20 cpe-20 h-40"
                          onClick={() => push(emptyExperience)}
                        />

                        <Button
                          text={
                            isEqual(values, initialValues) ? 'Cancel' : 'Reset'
                          }
                          btnStyle="primary-outline1"
                          className="cps-40 cpe-40 h-40"
                          onClick={() =>
                            isEqual(values, initialValues)
                              ? onHide()
                              : resetForm()
                          }
                          disabled={btnLoading}
                        />
                      </div>
                    </>
                  )}
                </FieldArray>
              </form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default PreviewProfessionalDetailsPopup;
